import {
  Button,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Spin,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import BreadCrumbs from '../../../../common/components/Breadcrumbs';
import { validateMessages } from '../../../../common/constants/common';
import { Page } from '../../../../common/constants/pageAction';
import { IConfigDataRetention } from '../../../../services/master/dataRetention/dataRetention.model';
import { useAppSelector, useAppDispatch } from '../../../../store/app.hooks';
import {
  getConfigDataRetentionById,
  updateConfigDataRetention,
} from '../../../../store/master/dataRetention/dataRetention.action';
import {
  clearConfigDataRetentionGetById,
  clearConfigDataRetentionMessages,
  configDataRetentionSelector,
} from '../../../../store/master/dataRetention/dataRetention.reducer';
import { getCutoffPreview } from '../MainTable';
import { IEditDataRetentionProps } from './editDataRetention.model';

const MAINTENANCE_RULE = 'Maintenance';
const EXEMPT_IDS_PATTERN = /^\s*(\d+\s*(,\s*\d+\s*)*)?$/;

const EditDataRetentionModal: React.FC<IEditDataRetentionProps> = (props) => {
  const configDataRetention = useAppSelector(configDataRetentionSelector);
  const dispatch = useAppDispatch();
  const { id, showModal, handleModalClose, refreshDataTable } = props;
  const data: IConfigDataRetention = configDataRetention.getById.data;
  const isMaintenance = data?.rule_type === MAINTENANCE_RULE;
  // Keep Latest Dataset is used by Age steps only (tables with a CompanyId).
  const isAgeRule = data?.rule_type === 'Age';

  const title = useMemo(() => {
    return (
      <>
        Edit <BreadCrumbs pageName={Page.ConfigDataRetention} level={1} />
      </>
    );
  }, []);

  const [form] = Form.useForm();
  const [retentionMonths, setRetentionMonths] = useState<number>(null);

  const onFinish = (values: any) => {
    const inputValues: IConfigDataRetention = {
      id: +id,
      retention_months: isMaintenance ? null : values.retention_months,
      is_enabled: !!values.is_enabled,
      exempt_company_ids: values.exempt_company_ids ?? '',
      notes: values.notes ?? '',
      batch_size: values.batch_size ?? null,
      keep_latest_dataset: isAgeRule ? !!values.keep_latest_dataset : undefined,
    };
    dispatch(updateConfigDataRetention(inputValues));
  };

  useEffect(() => {
    if (configDataRetention.save.messages.length > 0) {
      if (configDataRetention.save.hasErrors) {
        toast.error(configDataRetention.save.messages.join(' '));
      } else {
        toast.success(configDataRetention.save.messages.join(' '));
        handleModalClose();
        refreshDataTable();
      }
      dispatch(clearConfigDataRetentionMessages());
    }
  }, [configDataRetention.save.messages]);

  useEffect(() => {
    if (+id > 0 && data) {
      setRetentionMonths(data.retention_months);
      form.setFieldsValue({
        retention_months: data.retention_months,
        is_enabled: data.is_enabled,
        exempt_company_ids: data.exempt_company_ids,
        notes: data.notes,
        batch_size: data.batch_size,
        keep_latest_dataset: data.keep_latest_dataset,
      });
    }
  }, [data]);

  useEffect(() => {
    if (+id > 0) {
      dispatch(getConfigDataRetentionById(+id));
    }
    return () => {
      dispatch(clearConfigDataRetentionGetById());
    };
  }, [dispatch]);

  return (
    <>
      <Modal
        wrapClassName="custom-modal"
        title={title}
        centered
        visible={showModal}
        onCancel={handleModalClose}
        footer={false}
      >
        {configDataRetention.getById.loading || !data ? (
          <div className="spin-loader">
            <Spin spinning={true} />
          </div>
        ) : (
          <Form
            form={form}
            name="configDataRetention"
            onFinish={onFinish}
            onValuesChange={(changed) => {
              if ('retention_months' in changed) setRetentionMonths(changed.retention_months);
            }}
            validateMessages={validateMessages}
          >
            <Descriptions size="small" column={2} bordered className="mb-15">
              <Descriptions.Item label="Step">{data.step_name}</Descriptions.Item>
              <Descriptions.Item label="Table">{data.table_name}</Descriptions.Item>
              <Descriptions.Item label="Rule">{data.rule_type}</Descriptions.Item>
              <Descriptions.Item label="Date Column">{data.date_column ?? ''}</Descriptions.Item>
              <Descriptions.Item label="Order">{data.sort_order}</Descriptions.Item>
              <Descriptions.Item label="Deletes Older Than (if run today)">
                {getCutoffPreview({ ...data, retention_months: retentionMonths })}
              </Descriptions.Item>
            </Descriptions>
            <Row gutter={[30, 15]} className="form-label-hide">
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Retention Months
                  <Form.Item
                    name="retention_months"
                    label="Retention Months"
                    className="m-0"
                    rules={[{ required: !isMaintenance, type: 'number', min: 1, max: 120 }]}
                  >
                    <InputNumber
                      className="form-control w-100"
                      min={1}
                      max={120}
                      precision={0}
                      disabled={isMaintenance}
                      placeholder={isMaintenance ? 'Not used by Maintenance steps' : ''}
                    />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Exempt Company Ids
                  <Form.Item
                    name="exempt_company_ids"
                    label="Exempt Company Ids"
                    className="m-0"
                    rules={[
                      { max: 500 },
                      {
                        pattern: EXEMPT_IDS_PATTERN,
                        message: 'Comma-separated CompanyIds, numbers only (e.g. 73, 101)',
                      },
                    ]}
                  >
                    <Input className="form-control" placeholder="e.g. 73, 101" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group form-inline-pt m-0">
                  <Form.Item name="is_enabled" className="m-0" valuePropName="checked">
                    <Checkbox>Enabled</Checkbox>
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group form-inline-pt m-0">
                  <Form.Item name="keep_latest_dataset" className="m-0" valuePropName="checked">
                    <Checkbox disabled={!isAgeRule}>Keep Latest Dataset</Checkbox>
                  </Form.Item>
                  <small className="d-block">
                    {isAgeRule
                      ? "Never delete a Tenant/Company/BU's latest dataset, however old."
                      : 'Not used by this rule type.'}
                  </small>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Batch Size
                  <Form.Item
                    name="batch_size"
                    label="Batch Size"
                    className="m-0"
                    rules={[{ type: 'number', min: 10, max: 4500 }]}
                  >
                    <InputNumber
                      className="form-control w-100"
                      min={10}
                      max={4500}
                      precision={0}
                      placeholder="Default (4000)"
                    />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24}>
                <div className="form-group m-0">
                  Notes
                  <Form.Item name="notes" label="Notes" className="m-0" rules={[{ max: 500 }]}>
                    <Input.TextArea className="form-control" rows={3} />
                  </Form.Item>
                </div>
              </Col>
            </Row>
            <div className="btns-block modal-footer">
              <Button
                key="submit"
                type="primary"
                htmlType="submit"
                loading={configDataRetention.save.loading}
              >
                Update
              </Button>
              <Button key="back" onClick={handleModalClose}>
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </>
  );
};
export default EditDataRetentionModal;
